import type { MutableRefObject } from 'react'

import { TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

/**
 * TmapApp.getAccessKey()를 재시도 포함해서 호출.
 * iOS 인앱 브릿지 응답 타이밍 이슈 대응용.
 *
 * - retries: 시도 횟수 (기본 3)
 * - interval: 시도 간 대기 (기본 200ms)
 * - 하나라도 truthy 값을 받으면 즉시 반환
 * - 모두 실패 시 빈 문자열 반환 (throw 하지 않음)
 */
async function getAccessKeyWithRetry(options?: {
  retries?: number
  interval?: number
}): Promise<string> {
  const { retries = 3, interval = 200 } = options || {}

  for (let i = 0; i < retries; i++) {
    try {
      const key = await TmapApp.getAccessKey()
      if (key) return key as string
    } catch (_e) {}

    if (i < retries - 1) {
      await new Promise((resolve) => setTimeout(resolve, interval))
    }
  }

  return ''
}

type AccessKeyExpiredHandlerDeps = {
  accessKeyRef: MutableRefObject<string>
  setAccessKeyStorage: (value: string) => void
  setGlobalStateValue: (updater: (prev: any) => any) => void
  /** 테스트 주입용. 기본값은 getAccessKeyWithRetry. */
  getAccessKey?: (options?: { retries?: number; interval?: number }) => Promise<string>
}

/**
 * 401 리트라이 여부를 추적하기 위해 config에 부착하는 커스텀 플래그.
 * 원본 config를 mutation하지 않기 위해 spread로 새 config 객체에 세팅한다.
 */
type RetryableAxiosRequestConfig = AxiosRequestConfig & { _retriedFor401?: boolean }

/**
 * 라이브러리(`@tmap-web-lib/remote-api-client/frontman`)의 `onAccessKeyExpired` 콜백 시그니처.
 * 라이브러리가 이 타입을 export하지 않아 로컬에서 동일 형태로 재선언.
 */
type OnAccessKeyExpiredCallback = (
  error: AxiosError,
  axios: AxiosInstance
) => Promise<void> | void

/**
 * initFrontmanClient의 onAccessKeyExpired 콜백을 생성하는 factory.
 *
 * 라이브러리 프로토콜:
 * - 반환값이 undefined이면 원본 401을 Promise.reject로 전파
 * - Promise 반환 시 그것을 리트라이 결과로 사용
 *
 * 동작:
 * 1. 인앱이 아니면 undefined 반환 (브라우저에서는 리트라이 안 함)
 * 2. 이미 401 리트라이한 요청(_retriedFor401)이면 undefined 반환 (무한루프 방지)
 * 3. getAccessKey 재시도로 새 키 획득 시도
 *    - 실패 시 TmapApp.logout 호출 후 undefined 반환 (원본 401 전파)
 * 4. 성공 시 accessKeyRef/native/storage/atom 모두 동기화
 * 5. 원본 config를 mutation하지 않고, 플래그를 세팅한 새 config로 리트라이 후 그 결과 반환
 *
 * 반환 타입 캐스팅: 라이브러리 콜백 시그니처가 `Promise<void> | void`로 선언돼 있지만,
 * 라이브러리 인터셉터는 실제로는 반환된 Promise의 resolve 값을 리트라이 결과로 사용한다.
 * (JSDoc: "리턴값이 없으면 원본 에러로 Promise.reject 진행")
 * TypeScript 함수 반환 타입 변성 규칙상 `Promise<AxiosResponse>`를 `Promise<void>`에 직접
 * 대입할 수 없어 unknown 경유 캐스팅을 명시. 런타임 계약은 라이브러리 문서와 소스로 검증됨.
 */
function createOnAccessKeyExpired(deps: AccessKeyExpiredHandlerDeps): OnAccessKeyExpiredCallback {
  const { accessKeyRef, setAccessKeyStorage, setGlobalStateValue } = deps
  const getAccessKey = deps.getAccessKey ?? getAccessKeyWithRetry

  const handler = async (error: AxiosError, frontmanAxios: AxiosInstance) => {
    if (!TmapApp.env.isInApp) return

    const config = error.config as RetryableAxiosRequestConfig | undefined
    if (!config || config._retriedFor401) return

    const newAccessKey = await getAccessKey({ retries: 3, interval: 200 })
    if (!newAccessKey) {
      TmapApp.logout({
        reason: (error.response as AxiosResponse).data.error,
      })
      return
    }

    accessKeyRef.current = newAccessKey
    TmapApp.updateAccessKey({ key: newAccessKey })
    setAccessKeyStorage(newAccessKey)
    setGlobalStateValue((prev) => ({
      ...prev,
      accessKey: newAccessKey,
    }))

    // 원본 config를 mutation하지 않고 spread로 새 config 생성 (side effect 방지)
    const retryConfig: RetryableAxiosRequestConfig = { ...config, _retriedFor401: true }
    return frontmanAxios.request(retryConfig)
  }

  return handler as unknown as OnAccessKeyExpiredCallback
}

export { getAccessKeyWithRetry, createOnAccessKeyExpired }
export type { AccessKeyExpiredHandlerDeps, OnAccessKeyExpiredCallback }
